import { tz } from "@date-fns/tz";
import { addDays, formatISO, parseISO, subDays } from "date-fns";

import { BeMappedNode } from "src/constants/NodeDataTypes";
import {
  getBaseUrl,
  getUrlToAuthoringPage,
  getUrlToHistoricDecisionTrace,
} from "src/router/urls";
import { isParentNode } from "src/utils/predicates";
import { useHistorySelectedDecision } from "src/versionDecisionHistory/historySelectedDecisionStore";
import { useDecisionHistoryPaneEnv } from "src/versionDecisionHistory/useDecisionHistoryPaneEnv";

export const useGetChildVersionLink = () => {
  const decision = useHistorySelectedDecision();
  const { selectedEnv: decisionEnv } = useDecisionHistoryPaneEnv();
  return ({
    orgId,
    workspaceId,
    flowId,
    versionId,
  }: {
    orgId: string;
    workspaceId: string;
    flowId: string;
    versionId: string;
  }) => {
    if (decision === null) {
      return (
        getBaseUrl() +
        getUrlToAuthoringPage(orgId, workspaceId, flowId, versionId)
      );
    }
    // ------------------------------------------------
    // We need to give a grace period of a day to both from and to date.
    // For explanations see OPTI-4175 and https://github.com/taktile-org/taktile-services/pull/13203
    // The future solution should be passing full UTC timestamps in the URL,
    // we need a datepicker filter component for this to work.
    const fromDateTime: Date = subDays(parseISO(decision?.start_time), 1);
    const fromDate: string = formatISO(fromDateTime, {
      representation: "date",
      in: tz("Etc/UTC"),
    });
    const toDateTime: Date = addDays(parseISO(decision?.end_time), 1);
    const toDate: string = formatISO(toDateTime, {
      representation: "date",
      in: tz("Etc/UTC"),
    });
    // ------------------------------------------------
    return getUrlToHistoricDecisionTrace({
      orgId,
      wsId: workspaceId,
      decisionId: decision?.id,
      decisionEnv: decisionEnv,
      flowId: flowId,
      versionId: versionId,
      from: fromDate,
      to: toDate,
    });
  };
};

export const useChildVersionLinkFromNode = (
  selectedNode: BeMappedNode,
  orgId: string,
  wsId: string,
) => {
  const getChildFlowLink = useGetChildVersionLink();

  if (!isParentNode(selectedNode)) return undefined;
  if (
    !(
      selectedNode.data.child_flow_id && selectedNode.data.child_flow_version_id
    )
  )
    return undefined;
  return getChildFlowLink({
    orgId,
    workspaceId: wsId,
    flowId: selectedNode.data.child_flow_id,
    versionId: selectedNode.data.child_flow_version_id,
  });
};
